import { createElement, type ComponentType, type SVGProps } from "react";

import {
  Bolt,
  Bus,
  CalendarDays,
  FitnessCenter,
  Inventory2,
  Parking,
  Payments,
  Percent,
  Sofa,
  Sparkles,
  TagsIcon,
  WashingMachine,
  Wifi,
} from "@/components/icons";
import type {
  CampaignCategory,
  CampaignDiscountType,
  CampaignEffect,
  CampaignFeeType,
  CampaignPerkType,
  CampaignStatus,
  CampaignTargeting,
} from "@/types/campaign";

/**
 * The campaign catalogue — one source of truth for how every category looks and
 * what it asks the company to fill in.
 *
 * Both sides read from here: the portal builds its form from `fields`, and the
 * public advert picks its icon and tone from the same table. That is the whole
 * point of the file. When the two drift apart, a company ends up filling in a
 * percentage that the advert never shows.
 *
 * What is deliberately **not** here: any wording that describes a specific
 * campaign, and any arithmetic. Those come from the backend already localized
 * and already calculated, so that a discount reads identically on a card, on a
 * detail page and in anything we send by mail.
 */

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Which form fields a category asks for. Drives the portal's editor. */
export type CampaignField =
  | "steps"
  | "discountType"
  | "discountValue"
  | "durationMonths"
  | "freeMonths"
  | "perkType"
  | "perkMonthlyValue"
  | "feeType"
  | "waivedAmount";

export type CampaignCategoryEntry = {
  effect: CampaignEffect;
  icon: IconComponent;
  nameSv: string;
  nameEn: string;
  /** One line telling the author what this category is for. */
  hintSv: string;
  hintEn: string;
  fields: readonly CampaignField[];
};

export const campaignCategoryCatalog: Record<
  CampaignCategory,
  CampaignCategoryEntry
> = {
  RENT_DISCOUNT: {
    effect: "PRICE",
    icon: Percent,
    nameSv: "Rabatterad hyra",
    nameEn: "Reduced rent",
    hintSv:
      "Annonsen visar ordinarie hyra överstruken och det nya priset bredvid.",
    hintEn:
      "The advert shows the ordinary rent struck through beside the new one.",
    fields: ["steps", "discountType", "discountValue", "durationMonths"],
  },
  FREE_MONTHS: {
    effect: "PRICE",
    icon: CalendarDays,
    nameSv: "Hyresfria månader",
    nameEn: "Rent-free months",
    hintSv:
      "Månadshyran står kvar orörd — den är fortfarande vad man betalar övriga månader.",
    hintEn:
      "The monthly rent is left as it is — it is still what is paid in every other month.",
    fields: ["freeMonths"],
  },
  INCLUDED_PERK: {
    effect: "PERK",
    icon: Sparkles,
    nameSv: "Ingår i hyran",
    nameEn: "Included in the rent",
    hintSv: "Förmånen får en egen symbol på annonskortet.",
    hintEn: "The perk gets its own symbol on the advert card.",
    fields: ["perkType", "perkMonthlyValue", "durationMonths"],
  },
  WAIVED_FEE: {
    effect: "NEUTRAL",
    icon: Payments,
    nameSv: "Slopad avgift",
    nameEn: "Waived fee",
    hintSv: "Beloppet syns på annonssidan, inte på kortet.",
    hintEn: "The amount is shown on the advert page, not on the card.",
    fields: ["feeType", "waivedAmount"],
  },
  OTHER: {
    effect: "NEUTRAL",
    icon: TagsIcon,
    nameSv: "Övrigt erbjudande",
    nameEn: "Other offer",
    hintSv: "Er egen rubrik blir kampanjens märke. Använd när inget annat passar.",
    hintEn: "Your own title becomes the badge. Use when nothing else fits.",
    fields: [],
  },
};

/** Icons for the perks, so two perks never share one generic mark. */
const perkIcons: Record<CampaignPerkType, IconComponent> = {
  TRANSIT_CARD: Bus,
  BROADBAND: Wifi,
  ELECTRICITY: Bolt,
  GYM: FitnessCenter,
  PARKING: Parking,
  FURNITURE: Sofa,
  LAUNDRY: WashingMachine,
  STORAGE: Inventory2,
  OTHER: Sparkles,
};

/**
 * The backend prefixes perk icon keys. It has to: `OTHER` is both a category
 * and a perk type, so a bare key cannot distinguish "an offer we have no shape
 * for" from "something included that is not on the list".
 */
const PERK_ICON_PREFIX = "PERK_";

/**
 * Resolves the backend's `iconKey` to a component.
 *
 * Anything unrecognised falls back to the generic campaign mark rather than
 * rendering nothing — a badge with no icon looks broken, and a key we do not
 * know is a deploy-order problem between the two repositories, not a reason to
 * show the student a hole where an offer should be.
 */
export function campaignIcon(iconKey: string | null | undefined): IconComponent {
  if (!iconKey) return TagsIcon;

  if (iconKey.startsWith(PERK_ICON_PREFIX)) {
    const perk = iconKey.slice(PERK_ICON_PREFIX.length) as CampaignPerkType;
    return perkIcons[perk] ?? Sparkles;
  }

  if (iconKey in campaignCategoryCatalog) {
    return campaignCategoryCatalog[iconKey as CampaignCategory].icon;
  }

  return TagsIcon;
}

/** Builds the key the backend would send for a perk. Used by tests and previews. */
export function perkIconKey(perk: CampaignPerkType): string {
  return `${PERK_ICON_PREFIX}${perk}`;
}

/**
 * Draws the icon for a campaign's `iconKey`.
 *
 * A component rather than a bare lookup at each call site: `const Icon =
 * campaignIcon(key)` reads to the React lint rules as a component built during
 * render, since they cannot see that the lookup only ever returns one of a
 * fixed set of module-level components. Resolving inside one component keeps
 * every call site plain JSX.
 */
export function CampaignIcon({
  iconKey,
  className,
}: {
  iconKey?: string | null;
  className?: string;
}) {
  return createElement(campaignIcon(iconKey), {
    className,
    "aria-hidden": true,
  });
}

/** The same three tones for the detail page, where nothing sits over a photo. */
export const campaignBlockToneClass: Record<CampaignEffect, string> = {
  PRICE: "bg-brand-50 text-brand",
  PERK: "bg-white text-brand ring-1 ring-brand/20",
  NEUTRAL: "bg-gray-50 text-gray-600",
};

type Copy = { sv: string; en: string };

export const campaignPerkCopy: Record<CampaignPerkType, Copy> = {
  TRANSIT_CARD: { sv: "Kollektivtrafikkort", en: "Transit card" },
  BROADBAND: { sv: "Bredband", en: "Broadband" },
  ELECTRICITY: { sv: "El", en: "Electricity" },
  GYM: { sv: "Gymkort", en: "Gym membership" },
  PARKING: { sv: "Parkering", en: "Parking" },
  FURNITURE: { sv: "Möblering", en: "Furnishing" },
  LAUNDRY: { sv: "Tvätt", en: "Laundry" },
  STORAGE: { sv: "Förråd", en: "Storage" },
  OTHER: { sv: "Annat", en: "Other" },
};

export const campaignFeeCopy: Record<CampaignFeeType, Copy> = {
  DEPOSIT: { sv: "Deposition", en: "Deposit" },
  APPLICATION_FEE: { sv: "Ansökningsavgift", en: "Application fee" },
  QUEUE_FEE: { sv: "Köavgift", en: "Queue fee" },
  MOVE_IN_FEE: { sv: "Inflyttningsavgift", en: "Move-in fee" },
  OTHER: { sv: "Annan avgift", en: "Other fee" },
};

export const campaignDiscountTypeCopy: Record<CampaignDiscountType, Copy> = {
  PERCENT: { sv: "Procent av hyran", en: "Percent of the rent" },
  FIXED_AMOUNT: { sv: "Fast belopp i kronor", en: "Fixed amount in kronor" },
  FIXED_PRICE: { sv: "Fast kampanjhyra", en: "Flat campaign rent" },
};

export const campaignStatusCopy: Record<CampaignStatus, Copy> = {
  DRAFT: { sv: "Utkast", en: "Draft" },
  SCHEDULED: { sv: "Schemalagd", en: "Scheduled" },
  ACTIVE: { sv: "Aktiv", en: "Active" },
  PAUSED: { sv: "Pausad", en: "Paused" },
  ENDED: { sv: "Avslutad", en: "Ended" },
};

/**
 * The status dot beside the status word in the portal (a dot, like the listing
 * statuses, not a coloured chip). Only `ACTIVE` is what a student sees, so it
 * is the only one that gets the brand colour.
 */
export const campaignStatusToneClass: Record<CampaignStatus, string> = {
  DRAFT: "bg-pt-control",
  SCHEDULED: "bg-blue-500",
  ACTIVE: "bg-brand-600",
  PAUSED: "bg-pt-warn",
  ENDED: "bg-pt-prev",
};

export const campaignTargetingCopy: Record<CampaignTargeting, Copy> = {
  ALL_LISTINGS: { sv: "Alla våra annonser", en: "All our listings" },
  SELECTED: { sv: "Valda annonser", en: "Selected listings" },
};

/**
 * Why `ALL_LISTINGS` is usually the right answer: it also covers listings
 * imported after the campaign was written, which a fixed selection does not.
 */
export const campaignTargetingHint: Record<CampaignTargeting, Copy> = {
  ALL_LISTINGS: {
    sv: "Gäller även annonser som importeras senare.",
    en: "Also covers listings imported later.",
  },
  SELECTED: {
    sv: "Endast de annonser ni väljer nu.",
    en: "Only the listings you pick now.",
  },
};
